import { ArrowRight, Clock, Plus } from "lucide-react";
import { Layout } from "../components/Layout";
import { Picture } from "../components/Picture";
import { REGISTER_URL } from "../data/content";
import { AUTHOR, guidesBySlug, type Guide } from "../data/guides";

/** Single guide article (route "/ratgeber/<slug>"). */
export function GuideArticle({ guide }: { guide: Guide }): JSX.Element {
  const related = guide.related
    .map((slug) => guidesBySlug[slug])
    .filter((g): g is Guide => Boolean(g));

  return (
    <Layout>
      <article className="py-14 sm:py-20 lg:py-24">
        <div className="container-welope">
          <div className="mx-auto max-w-2xl">
            <nav aria-label="Brotkrumen" className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 font-body text-label-sm text-on-surface-variant">
                <li>
                  <a href="/" className="transition-colors hover:text-primary">
                    Start
                  </a>
                </li>
                <li aria-hidden>›</li>
                <li>
                  <a href="/ratgeber" className="transition-colors hover:text-primary">
                    Ratgeber
                  </a>
                </li>
                <li aria-hidden>›</li>
                <li className="text-primary">{guide.title}</li>
              </ol>
            </nav>

            <p className="section-eyebrow">{guide.category}</p>
            <h1 className="mt-3 font-headline text-[34px] font-extrabold leading-[1.1] tracking-[-0.02em] text-primary sm:text-[46px]">
              {guide.title}
            </h1>
            <p className="mt-4 flex items-center gap-1.5 font-body text-label-sm text-on-surface-variant">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              {guide.readingMinutes} Min. Lesezeit · Von {AUTHOR.name}
            </p>

            <div className="mt-8 overflow-hidden rounded-2xl">
              <Picture
                src={guide.heroImage.src}
                alt={guide.heroImage.alt}
                width={800}
                height={500}
                className="h-full w-full object-cover"
              />
            </div>

            <p className="mt-8 font-body text-body-lg leading-[1.6] text-on-surface">
              {guide.intro}
            </p>

            <div className="mt-10 space-y-10">
              {guide.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="font-headline text-headline-lg leading-tight text-primary">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, i) => (
                    <p
                      key={i}
                      className="mt-4 font-body text-body-md leading-[1.65] text-on-surface-variant"
                    >
                      {para}
                    </p>
                  ))}
                  {section.list && (
                    <ul className="mt-4 space-y-2">
                      {section.list.map((item) => (
                        <li
                          key={item}
                          className="flex gap-3 font-body text-body-md leading-[1.55] text-on-surface-variant"
                        >
                          <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-secondary" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

            {/* FAQ */}
            <section className="mt-14" aria-labelledby="guide-faq-title">
              <h2
                id="guide-faq-title"
                className="font-headline text-headline-lg text-primary"
              >
                Häufige Fragen
              </h2>
              <div className="mt-6 space-y-3">
                {guide.faqs.map((item) => (
                  <details
                    key={item.question}
                    className="group rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-md py-1 [&_summary::-webkit-details-marker]:hidden"
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 font-headline text-headline-md text-on-surface">
                      {item.question}
                      <Plus
                        className="h-5 w-5 shrink-0 text-secondary transition-transform duration-200 group-open:rotate-45"
                        aria-hidden="true"
                      />
                    </summary>
                    <p className="pb-4 pr-8 font-body text-body-md leading-[1.6] text-on-surface-variant">
                      {item.answer}
                    </p>
                  </details>
                ))}
              </div>
            </section>

            {/* Inline CTA */}
            <aside className="mt-14 rounded-2xl bg-primary p-lg text-on-primary shadow-bloom">
              <p className="font-headline text-headline-md leading-tight">
                Welope – das soziale Netzwerk für Haustiere.
              </p>
              <p className="mt-2 font-body text-body-md text-primary-fixed">
                Self-hosted in Deutschland, DSGVO-konform. Sei früh dabei.
              </p>
              <a href={REGISTER_URL} className="btn-secondary mt-5 inline-flex">
                Auf die Warteliste
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </aside>

            {/* Related guides */}
            {related.length > 0 && (
              <section className="mt-14" aria-labelledby="related-title">
                <h2
                  id="related-title"
                  className="font-headline text-headline-md text-primary"
                >
                  Weiterlesen
                </h2>
                <ul className="mt-5 space-y-3">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <a
                        href={`/ratgeber/${r.slug}`}
                        className="group flex items-center justify-between gap-4 rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-md py-4 transition-colors hover:border-primary/40"
                      >
                        <span className="font-body text-body-md font-medium text-on-surface">
                          {r.title}
                        </span>
                        <ArrowRight
                          className="h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5"
                          aria-hidden="true"
                        />
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      </article>
    </Layout>
  );
}
